import * as React from "react";
import { SectionShell } from "@/components/design-system/SectionShell";
import { Autocomplete, type AutocompleteSuggestion } from "@/components/ui/autocomplete";

// Stand-ins for what a server would return. In a real screen these arrive from
// a fetch, which is the whole reason this primitive exists.
const PEOPLE: AutocompleteSuggestion[] = [
  { id: 1, name: "Dana Whitfield", hint: "dwhitfield@iupat.org · DC 16" },
  { id: 2, name: "Marcus Reyes", hint: "mreyes@iupat.org · DC 21" },
  { id: 3, name: "Priya Raman", hint: "praman@iupat.org · IUPAT" },
  { id: 4, name: "Tom Whitfield", hint: "twhitfield@iupat.org · LU 1968" },
].map((person) => ({ id: person.id, label: person.name, hint: person.hint }));

const code = `import { Autocomplete } from "@/components/ui/autocomplete";
import { useUserSearch } from "@/hooks/use-user-search";

const [query, setQuery] = React.useState("");
const { suggestions, loading } = useUserSearch(query); // debounced fetch

<Autocomplete
  id="user-search"
  value={query}
  onValueChange={setQuery}
  suggestions={suggestions.map((person) => ({
    id: person.id,
    label: person.name,
    hint: person.email,
  }))}
  loading={loading}
  onSelect={(suggestion) => router.visit(\`/admin/users/\${suggestion.id}\`)}
  placeholder="Name, email, username or organization"
  hintMessage="Type a name to search."
  emptyMessage="No accounts match."
  aria-label="Search accounts"
/>`;

export function AutocompleteSection() {
  const [query, setQuery] = React.useState("");
  const [chosen, setChosen] = React.useState<string | null>(null);

  const suggestions = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return PEOPLE.filter((person) => person.label.toLowerCase().includes(needle));
  }, [query]);

  return (
    <SectionShell
      id="autocomplete"
      title="Autocomplete"
      description={
        <>
          A search box whose suggestions come from the server. The caller owns
          the text, the debounce and the fetch; the primitive owns the popover,
          the keyboard model (<code>↑</code> / <code>↓</code> / <code>Enter</code>{" "}
          / <code>Esc</code>) and the three empty states — nothing typed,
          searching, and no matches. Typing does not select anything, so a page
          can use the same text as a list filter while the dropdown offers
          jump-to targets.
        </>
      }
      whenToUse={
        <ul>
          <li>
            Finding one record among more than you would ship to the browser —
            several hundred user accounts, say.
          </li>
          <li>Anywhere the matching has to happen in the database.</li>
          <li>
            Alongside a paginated list the same text narrows — see{" "}
            <code>/admin/users</code>.
          </li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>
            When the whole list is already in the browser — use{" "}
            <code>Combobox</code>, which filters options it holds.
          </li>
          <li>For a handful of options — use <code>Select</code>.</li>
          <li>For actions rather than records — use <code>DropdownMenu</code>.</li>
        </ul>
      }
      preview={
        <div className="flex flex-wrap items-start gap-8">
          <div className="flex w-80 flex-col gap-2">
            <Autocomplete
              value={query}
              onValueChange={setQuery}
              suggestions={suggestions}
              onSelect={(suggestion) => {
                setChosen(suggestion.label);
                setQuery(suggestion.label);
              }}
              placeholder="Name, email or organization"
              hintMessage="Type a name — try “whit”."
              emptyMessage="No accounts match."
              aria-label="Search people"
            />
            <span className="text-xs text-ink-muted">
              {chosen ? `Picked ${chosen}` : "Type to see suggestions"}
            </span>
          </div>

          <div className="flex w-80 flex-col gap-2">
            <Autocomplete
              value="Dana"
              onValueChange={() => {}}
              suggestions={[]}
              onSelect={() => {}}
              loading
              aria-label="Searching"
            />
            <span className="text-xs text-ink-muted">Loading</span>
          </div>

          <div className="flex w-80 flex-col gap-2">
            <Autocomplete
              value=""
              onValueChange={() => {}}
              suggestions={[]}
              onSelect={() => {}}
              disabled
              placeholder="Unavailable"
              aria-label="Disabled"
            />
            <span className="text-xs text-ink-muted">Disabled</span>
          </div>
        </div>
      }
      code={code}
    />
  );
}
